// src/components/ForceReloadToast.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { clearCacheAndReloadForVerification } from "../../utils/clearCacheAndReload";


export default function ForceReloadToast(): JSX.Element | null {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent<{ message?: string }>).detail;
      setMsg(payload?.message ?? "La aplicación encontró un error crítico.");
      setVisible(true);
    };
    window.addEventListener("app:fatalError", handler as EventListener);
    return () => window.removeEventListener("app:fatalError", handler as EventListener);
  }, []);

  if (!visible) return null;

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 1400,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          p: 2,
          minWidth: 320,
          maxWidth: 420,
          backgroundColor: "#ffffff",
          color: "#FFFFFF",
          borderRadius: 2,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        {/* título */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#336B87", 
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: "bold",
            }}
          >
            Error de aplicación
          </Typography>

          {/* botón cerrar (small) */}
          <IconButton
            aria-label="cerrar-toasty"
            size="small"
            onClick={() => setVisible(false)}
            sx={{
              color: "#FFFFFF",
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* mensaje */}
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            color: "rgba(0,0,0,0.6)", 
          
          }}
        >
          {msg}
          <Box component="span" display="block" sx={{ mt: 1 }}>
            Para volver a la versión estable, recarga la aplicación.
          </Box>
        </Typography>

        {/* acciones: centradas */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 1 }}>
          {/* Cancelar */}
          <Button
            onClick={() => setVisible(false)}
            variant="contained"
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#FB3748",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "'Montserrat', sans-serif",
              borderRadius: 1,
              px: 2,
              py: 0.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#FB3748",
                color: "#FFFFFF",
                boxShadow: "none",
              },
            }}
          >
            Cancelar
          </Button>

          {/* Confirmar */}
          <Button
            onClick={() => {
              clearCacheAndReloadForVerification();
            }}
            variant="contained"
            sx={{
              backgroundColor: "#336B87",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "'Montserrat', sans-serif",
              borderRadius: 1,
              px: 2,
              py: 0.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1FC16B",
                color: "#FFFFFF",
                boxShadow: "none",
              },
            }}
          >
            Vaciar caché y recargar
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
